import React from "react";
import { Trans } from "react-i18next";
import i18n from "../../i18n";

import {
  H3,
  Button,
  Divider,
  NumericInput,
  Alert,
  Callout,
  FormGroup,
  HTMLSelect,
  Spinner,
} from "@blueprintjs/core";
import { DateRangePicker3 } from "@blueprintjs/datetime2";
import { Cell, Column, RowHeaderCell, Table2 } from "@blueprintjs/table";

import {
  authenticatedGetFetch,
  authenticatedPostFetch,
} from "../../utils/AuthenticatedFetch";
import { ParkSelect } from "../Structure/ParkSelector";

export default class BookingStock extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedRange: [undefined, undefined], // Initialiser la plage de dates avec deux valeurs nulles
      availabilityData: [], // Données de disponibilité
      selectedPassType: "onepass",
      filterValueParks: undefined,
      server: undefined,
      stock: 0,
      hysteresis: 0,
      display: 0,
    };
  }

  // Simuler l'appel API pour récupérer les données de disponibilité
  fetchAvailabilityData = () => {
    if (
      this.state.selectedRange?.[0] &&
      this.state.selectedRange?.[1] &&
      this.state.filterValueParks &&
      this.state.selectedPassType
    ) {
      const from = new Date(this.state.selectedRange[0]);
      const to = new Date(this.state.selectedRange[1]);
      authenticatedGetFetch(
        "/booking/resaweb/stock?" +
          new URLSearchParams({
            from: from.toISOString(),
            to: to.toISOString(),
            type: this.state.selectedPassType,
            park: this.state.filterValueParks,
            srv: this.state.server,
          }),
        (response) => {
          if (response?.datas) {
            this.setState({ availabilityData: response.datas }, () => {
              this.preprocessData();
            });
          } else {
            this.setState({ availabilityData: [], display: -1 });
          }
        },
        null,
        null,
        () => {
          this.setState({ availabilityData: [], display: -1 });
        },
        false,
        { cache: "reload" }
      );
    }
  };

  // Simuler l'appel API pour mettre à jour le stock
  UpdateStock = () => {
    const from = new Date(this.state.selectedRange[0]);
    const to = new Date(this.state.selectedRange[1]);
    authenticatedPostFetch(
      "/booking/resaweb/updateStock",
      {
        from: from.toISOString(),
        to: to.toISOString(),
        type: this.state.selectedPassType,
        park: this.state.filterValueParks,
        stock: this.state.stock,
        srv: this.state.server,
      },
      (code, authResponse) => {
        this.setState({ availabilityData: [], display: 1 }, () =>
          this.fetchAvailabilityData()
        );
      }
    );
  };

  // Formater une date en fonction du format souhaité
  formatTime = (dateString) => {
    if (dateString) {
      const date = new Date(dateString);
      return date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    }
  };

  preprocessData() {
    const dataMap = {};
    this.state.availabilityData.forEach((slot) => {
      const date = new Date(slot.from).toLocaleDateString();
      const time = this.formatTime(slot.from);
      if (!dataMap[date]) dataMap[date] = {};
      dataMap[date][time] = slot;
    });
    this.setState({ dataMap });
  }

  colorCell(reserved, maximum) {
    if (reserved >= maximum) return "danger";
    else if (reserved >= maximum - this.state.hysteresis) return "warning";
    else return "none";
  }

  render() {
    // Extrait un jour unique depuis data
    const days = [
      ...new Set(
        this.state.availabilityData.map((slot) =>
          new Date(slot.from).toLocaleDateString()
        )
      ),
    ];
    // Extrait une heure unique depuis data
    const hours = [
      ...new Set(
        this.state.availabilityData.map((slot) => this.formatTime(slot.from))
      ),
    ];

    return (
      <>
        <Callout
          icon="timeline-events"
          title={<Trans>gestion des stocks</Trans>}
        >
          {/* filters */}
          <div className="flexbox-center">
            <DateRangePicker3
              minDate={new Date(new Date().setMonth(new Date().getMonth() - 7))} // Restreindre la date minimale
              maxDate={new Date(new Date().setMonth(new Date().getMonth() + 7))} // Restreindre la date maximale
              value={this.state.selectedRange} // Définir la plage sélectionnée
              onChange={(date) =>
                this.setState({ selectedRange: date, display: 0 })
              } // Mettre à jour la plage de dates à chaque sélection
              highlightCurrentDay={true} // Mettre en surbrillance la journée actuelle
              allowSingleDayRange={true} // Permet la sélection d'une seule journée
              timePickerProps={{ precision: "minute" }}
              shortcuts={false}
            />
            <div>
              <FormGroup
                label={<Trans>type :</Trans>}
                labelFor="input_type"
                inline={true}
              >
                <HTMLSelect
                  id="input_type"
                  options={[
                    { value: "onepass", label: i18n.t("OnePass") },
                    { value: "multipass", label: i18n.t("MultiPass") },
                  ]}
                  onChange={(ti) =>
                    this.setState({
                      selectedPassType: ti.target.value,
                      display: 0,
                    })
                  }
                  value={this.state.selectedPassType}
                />
              </FormGroup>

              <FormGroup
                label={<Trans>park:</Trans>}
                labelFor="par"
                inline={true}
              >
                <ParkSelect
                  id="par"
                  filter={(park) => park?.server?.id === this.props?.server?.id}
                  onSelect={(parkId, data) => {
                    this.setState({
                      selectedpk: data,
                      filterValueParks: parkId,
                      server: data.server.id,
                      display: 0,
                    });
                  }}
                />
              </FormGroup>

              <FormGroup
                label={<Trans>hysteresis :</Trans>}
                labelFor="hys"
                helperText={
                  <p>
                    signaler les périodes ou le nombre de
                    <br />
                    places disponibles est inférieure à ce seuil
                  </p>
                }
                inline={true}
              >
                <NumericInput
                  id="hys"
                  value={this.state.hysteresis} // Valeur actuelle
                  onValueChange={(newValue) =>
                    this.setState({ hysteresis: newValue })
                  } // Mettre à jour l'état
                  stepSize={1} // Taille du pas lors de l'incrémentation
                  majorStepSize={10} // Pas lors de l'incrémentation majeure (maintenir Shift)
                  min={0}
                  intent="primary"
                />
              </FormGroup>

              <Button
                intent="success"
                rightIcon="arrow-right"
                text={<Trans>afficher les stocks</Trans>}
                disabled={
                  !(
                    this.state.selectedRange?.[0] &&
                    this.state.selectedRange?.[1] &&
                    this.state.filterValueParks &&
                    this.state.selectedPassType &&
                    this.state.selectedRange?.[0].getTime() !==
                      this.state.selectedRange?.[1].getTime()
                  )
                }
                onClick={() => {
                  this.fetchAvailabilityData();
                  this.setState({ availabilityData: [], display: 1 });
                }}
              />
            </div>

            <Divider />
            <div>
              <FormGroup
                label={<Trans>valeur du nouveau stock :</Trans>}
                labelFor="nsv"
                helperText={
                  <Trans>valeur à attribuer sur la période séléctionnée</Trans>
                }
                inline={true}
              >
                <NumericInput
                  id="nsv"
                  value={this.state.stock} // Valeur actuelle
                  onValueChange={(newValue) =>
                    this.setState({ stock: newValue })
                  } // Mettre à jour l'état
                  stepSize={1} // Taille du pas lors de l'incrémentation
                  majorStepSize={10} // Pas lors de l'incrémentation majeure (maintenir Shift)
                  min={0}
                  intent="primary"
                />
              </FormGroup>
              <Button
                intent="success"
                rightIcon="arrow-right"
                text={<Trans>modifier les stocks</Trans>}
                disabled={
                  !(
                    this.state.selectedRange?.[0] &&
                    this.state.selectedRange?.[1] &&
                    this.state.filterValueParks &&
                    this.state.selectedPassType &&
                    this.state.display > 0
                  )
                }
                onClick={() => {
                  this.UpdateStock();
                }}
              />
            </div>
          </div>
          <br />
        </Callout>
        

        {this.state.selectedRange?.[0] &&
          this.state.selectedRange?.[1] &&
          this.state.filterValueParks &&
          this.state.selectedPassType &&
          this.state.display === 1 && (
              <Callout
                title={
                  <>
                    <Trans>stock pour la période sélectionnée (Du </Trans>
                    {this.state.selectedRange[0].toLocaleDateString()}{" "}
                    <Trans>au</Trans>{" "}
                    {this.state.selectedRange[1].toLocaleDateString()})
                  </>
                }
              >
                {this.state.availabilityData.length === 0 ? (
                  <div>
                    <Spinner intent="primary" />
                    <Trans>chargement des créneaux...</Trans>
                  </div>
                ) : (
                  <Table2
                    numRows={days.length} // Nombre de lignes dans la table
                    defaultColumnWidth={70}
                    rowHeaderCellRenderer={(rowIndex) => (
                      <RowHeaderCell index={rowIndex} name={days[rowIndex]} />
                    )}
                  >
                    {hours.map((hour, hourIndex) => (
                      <Column
                        key={hourIndex}
                        name={hour} // Définit le nom de l'en-tête de chaque colonne
                        cellRenderer={(rowIndex) => {
                          const date = days[rowIndex];
                          const time = hour;
                          const slot = this.state.dataMap?.[date]?.[time];

                          if (slot) {
                            const type = slot.onepass
                              ? slot.onepass
                              : slot.multipass;
                            return (
                              <Cell
                                intent={this.colorCell(
                                  type.reserved,
                                  type.maximum
                                )}
                              >
                                <div>
                                  {type.reserved}/{type.maximum}
                                </div>
                              </Cell>
                            );
                          } else {
                            return (
                              <Cell>
                                <Trans>aucune donnée</Trans>
                              </Cell>
                            );
                          }
                        }}
                      />
                    ))}
                  </Table2>
                )}
              </Callout>
          )}

        <Alert
          confirmButtonText={i18n.t("close")}
          isOpen={this.state.display === -1}
          onClose={() => this.setState({ display: 0 })}
          onConfirm={() => this.setState({ display: 0 })}
          canEscapeKeyCancel={true}
          canOutsideClickCancel={true}
          intent="danger"
          icon="error"
        >
          <strong>
            <Trans>error occurred while loading stocks</Trans>
          </strong>
        </Alert>
      </>
    );
  }
}
